// Frontend companion to functions/autoSend/autoSendPolicy.js — the shared vocabulary
// for the company switch `features.autoSendToCrm` and the per-lead outcome marker
// `data.autoSend` the backend trigger writes. KEEP THE BOUNDS + REASON KEYS IN SYNC
// with that module.

export const AUTO_SEND_DEFAULT_MIN_PERCENT = 70;
// The matcher only stores 50%+ matches, so a lower bar is meaningless.
export const AUTO_SEND_MIN_PERCENT_BOUNDS = { min: 50, max: 100 };

export const isValidAutoSendPercent = (raw) => {
    const n = Number(raw);
    return Number.isInteger(n) && n >= AUTO_SEND_MIN_PERCENT_BOUNDS.min && n <= AUTO_SEND_MIN_PERCENT_BOUNDS.max;
};

// Firestore Timestamp | Date | number | string → Date | null
export const autoSendToDate = (v) => {
    if (!v) return null;
    if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
    if (typeof v.toDate === 'function') { try { return v.toDate(); } catch { return null; } }
    if (typeof v === 'object' && (typeof v.seconds === 'number' || typeof v._seconds === 'number')) {
        return new Date((v.seconds ?? v._seconds) * 1000);
    }
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
};

const fmtDate = (v) => {
    const d = autoSendToDate(v);
    if (!d) return '';
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

// Plain-English label for a backend skip/failure reason key.
export const describeAutoSendReason = (reason, marker = {}) => {
    switch (reason) {
        case 'no_buyers_at_threshold': {
            const bar = marker.minMatchPercent ?? AUTO_SEND_DEFAULT_MIN_PERCENT;
            const top = typeof marker.topMatchPercent === 'number' ? ` (best match was ${marker.topMatchPercent}%)` : '';
            return `no buyer matched ${bar}% or more${top}`;
        }
        case 'portfolio_member': return 'packages are sent by hand';
        case 'no_ghl_connected': return 'GoHighLevel is not connected';
        case 'config_missing_pipeline_or_stage': return 'auto-send has no pipeline or stage set';
        case 'config_missing_enabled_at': return 'auto-send settings are incomplete — turn it off and on again';
        case 'configured_pipeline_or_stage_not_found': return 'the pipeline or stage chosen for auto-send no longer exists in GoHighLevel';
        case 'no_opportunities_created': return 'no opportunities were created (buyers had no email or phone, or GoHighLevel rejected them)';
        case 'no_result': return 'the send returned nothing';
        default: return reason ? String(reason) : 'unknown reason';
    }
};

/**
 * One line + tone for the lead page banner.
 * @returns {{ text:string, tone:'success'|'error'|'neutral', colourClass:string }}
 */
export const describeAutoSendMarker = (marker) => {
    if (!marker || typeof marker !== 'object') return null;
    const when = fmtDate(marker.sentAt || marker.evaluatedAt || marker.claimedAt);
    const whenSuffix = when ? ` on ${when}` : '';
    switch (marker.status) {
        case 'sent': {
            const n = Number(marker.created) || 0;
            const failedNote = Number(marker.failed) > 0 ? `, ${marker.failed} failed` : '';
            const skippedNote = Number(marker.skipped) > 0 ? `, ${marker.skipped} skipped` : '';
            return {
                text: `Sent automatically to ${n} buyer${n === 1 ? '' : 's'}${whenSuffix}${failedNote}${skippedNote}. Sending again will create duplicate opportunities.`,
                tone: 'success',
                colourClass: 'colour-green',
            };
        }
        // Large recipient lists go out in chunks; a chunk hard-failed after earlier
        // ones had already created opportunities.
        // ⚠️ `created` is a COUNT, not a prefix of buyerIds — a chunk can succeed only
        // partly (GHL rejects a buyer with no email/phone) and still count as sent, so
        // the buyers who got the deal are scattered, not the first N on the list.
        // Never name them here; the CRM is the only reliable answer, and marker.results
        // caps at 60 so it cannot enumerate a large send either.
        case 'partial': {
            const n = Number(marker.created) || 0;
            const total = Number(marker.buyerCount) || 0;
            const ofTotal = total > n ? ` of ${total}` : '';
            return {
                text: `Automatic send only reached ${n}${ofTotal} buyer${n === 1 ? '' : 's'}${whenSuffix}: ${describeAutoSendReason(marker.error, marker)}. Check GoHighLevel for which buyers already have the deal before sending the rest by hand — resending to one that already has it creates a duplicate.`,
                tone: 'error',
                colourClass: 'colour-error',
            };
        }
        case 'pending':
            return { text: 'Automatic send in progress…', tone: 'neutral', colourClass: 'colour-secondary' };
        case 'timeout':
            return {
                text: `Automatic send timed out${whenSuffix}. GoHighLevel may still have created the opportunities — check the pipeline before sending by hand, or you may create duplicates.`,
                tone: 'error',
                colourClass: 'colour-error',
            };
        case 'failed':
            return {
                text: `Automatic send failed${whenSuffix}: ${describeAutoSendReason(marker.error, marker)}. Nothing went out — send it by hand below.`,
                tone: 'error',
                colourClass: 'colour-error',
            };
        // A skip is not final: nothing was sent, so if the seller re-blasts the
        // property at a new price the backend re-underwrites it and looks again
        // (functions/autoSend/autoSendPolicy.js — RE_EVALUABLE_STATUSES). Packages
        // are the exception: they are sent by hand however many times they arrive.
        case 'skipped': {
            const retry = marker.reason === 'portfolio_member' ? '' : ' If it comes in again at a new price, it gets checked again.';
            return {
                text: `Not sent automatically: ${describeAutoSendReason(marker.reason, marker)}.${retry}`,
                tone: 'neutral',
                colourClass: 'colour-secondary',
            };
        }
        default:
            return { text: 'Automatic send: unknown state.', tone: 'neutral', colourClass: 'colour-secondary' };
    }
};
